import React, {
  useRef, useCallback, useState, useEffect, Fragment,
} from 'react';
import {
  Drawer, Classes, FormGroup, InputGroup, Icon, Button, Intent, H3, TextArea,
  Tabs, Tab, Spinner, Card, Alert,
  Checkbox,
} from '@blueprintjs/core';
import {gql} from 'apollo-boost';
import {useQuery} from '@apollo/react-hooks';
import {useDropzone} from 'react-dropzone';
import classNames from 'classnames';

import {bugsnagClient} from 'utils/bugsnag';
import {WSGQL} from 'utils/ws-api';
import {showGQLError, showError, showSuccess, showWSErrors} from 'utils/toaster';
import {useUIActions} from 'utils/ui-context';

const FETCH_FILE_QUERY = gql`
  query FileEditDialogFetch($clientId: ID!, $fileId: ID!) {
    files(clientId: $clientId, fileId: $fileId) {
      name,
      creator {
        fullName,
      },
      url,
      identifier,
      linkUrl,
      thumbnailUrl,
      notes,
      confidential,
      pages {
        id,
        name,
        parentId,
        pageType,
        guide {
          id,
          name,
        },
      }
    }
  }
`;

const SEARCH_EXISTING_FILES_QUERY = gql`
  query FileEditDialogSearchExistingFiles($clientId: ID!, $query: String!) {
    files(clientId: $clientId, searchQuery: $query) {
      id,
      name,
      notes,
      thumbnailUrl,
      identifier,
    }
  }
`;

const FETCH_UPLOAD_URL_QUERY = gql`
  query FilesEditDialogFetchUploadUrl($clientId: ID!) {
    fileUploadUrl(clientId: $clientId) {
      url,
      uuid,
    }
  }
`;

const UPDATE_FILE_MUTATION = gql`
  mutation FilesEditDialogUpdateFile(
    $clientId: ID!,
    $fileId: ID!,
    $attributes: FileAttributes!,
  ) {
    updateFile(clientId: $clientId, fileId: $fileId, attributes: $attributes) {
      file {
        id,
      },
      errors {
        title,
        message,
      }
    }
  }
`;

const CREATE_FILE_MUTATION = gql`
  mutation FilesEditDialogUpdateFile(
    $clientId: ID!,
    $attributes: FileAttributes!,
    $addToPageId: ID!,
  ) {
    createFile(clientId: $clientId, attributes: $attributes, addToPageId: $addToPageId) {
      file {
        id,
      },
      errors {
        title,
        message,
      }
    }
  }
`;

const ADD_FILE_TO_PAGE_MUTATION = gql`
  mutation FilesEditDialogAddToPage($clientId: ID!, $fileId: ID!, $pageId: ID!) {
    addFileToPage(clientId: $clientId, fileId: $fileId, pageId: $pageId) {
      file {
        id
      }
    }
  }
`;

const DELETE_ENTIRELY_MUTATION = gql`
  mutation FileEditDialogDeleteEntirely($fileId: ID!, $clientId: ID!) {
    entirelyDeleteFile(fileId: $fileId, clientId: $clientId) {
      success
    }
  }
`;

const FileType = {
  FILE: 'file',
  COPY: 'copy',
  LINK: 'link',
};

const FileEditDialog = ({
  clientId,
  fileId,
  addToPageId,
  isOpen,
  onSuccess,
}) => {
  ////
  // Hooks
  //
  const isEditing = !!fileId;
  const [form, setForm] = useState({
    name: '',
    identifier: '',
    notes: '',
    blobId: null,
    linkUrl: '',
    existingFileId: null,
    confidential: false,
  });
  const currentForm = useRef();
  currentForm.current = form;
  const [fileType, setFileType] = useState(FileType.FILE);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadPerc, setUploadPerc] = useState(0);
  const [fileSearchQuery, setFileSearchQuery] = useState('');
  const [fileSearchResults, setFileSearchResults] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const {clearDialog} = useUIActions();

  const {loading: loadingFile} = useQuery(FETCH_FILE_QUERY, {
    fetchPolicy: 'network-only',
    skip: !isEditing,
    variables: {clientId, fileId},
    onError: error => showGQLError({error}),
    onCompleted: ({files}) => {
      const [file] = files;
      if (file.linkUrl) {
        setFileType(FileType.LINK);
      }

      setForm({...form, ...file});
    },
  });

  const searchTimeout = useRef(null);
  useEffect(() => {
    if (searchTimeout.current) window.clearInterval(searchTimeout.current);

    searchTimeout.current = window.setTimeout(async () => {
      if (fileSearchQuery.length === 0) return;
      setLoadingSearch(true);
      let resp;
      try {
        resp = await WSGQL.query({
          query: SEARCH_EXISTING_FILES_QUERY,
          variables: {clientId, query: fileSearchQuery},
        });
      } catch (error) {
        showGQLError({error});
        return;
      }

      setLoadingSearch(false);
      setFileSearchResults(resp.data.files);
    }, 500);

    return () => {
      if (searchTimeout.current) {
        window.clearTimeout(searchTimeout.current);
      }
    };
  }, [fileSearchQuery]);

  ////
  // Events
  //
  const openFile = () => {
    window.open(form.url, '_blank');
  };

  const selectExistingFile = (file) => {
    setForm({
      ...form,
      existingFileId: file.id,
      name: file.name,
      notes: file.notes,
      identifier: file.identifier,
    });
  };

  const changeFileType = (newType) => {
    // If they've already uploaded a file let's simplify things by saying they
    // can't change the file type without reloading the dialog
    if (form.blobId) return;

    setFileType(newType);
    // TODO: Logic here
  };

  const uploadFile = async (file) => {
    setUploadingFile(true);
    const {data} = await WSGQL.query({
      query: FETCH_UPLOAD_URL_QUERY,
      variables: {clientId},
      fetchPolicy: 'network-only',
    });
    const {url, uuid} = data.fileUploadUrl;

    const formData = new FormData();
    formData.append('file', file);

    const req = new XMLHttpRequest();

    req.upload.addEventListener('error', (e) => {
      setUploadingFile(0);
      setUploadPerc(0);
      showError({message: 'There was an error uploading your file. Please refresh the page and try again.'});
      bugsnagClient.notify(e);
    });

    req.upload.addEventListener('progress', (e) => {
      if (e.loaded < file.size) {
        setUploadPerc(e.loaded / file.size);
      }
    });

    req.upload.addEventListener('load', () => {
      setUploadingFile(false);
      setUploadPerc(0);
      setForm({...currentForm.current, filename: file.name, blobId: uuid});
    });

    req.open('PUT', url);
    req.send(file);
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 1) {
      alert('You may only upload one file at a time.');
      return;
    }

    uploadFile(acceptedFiles[0]);
  }, []);
  const {getRootProps, getInputProps, isDragActive} = useDropzone({onDrop});

  const addToPage = async () => {
    setLoadingUpdate(true);
    try {
      await WSGQL.mutate({
        mutation: ADD_FILE_TO_PAGE_MUTATION,
        variables: {clientId, fileId: form.existingFileId, pageId: addToPageId},
      });
    } catch (error) {
      setLoadingUpdate(false);
      showGQLError({error});
      return;
    }

    showSuccess({message: 'File added to page'});
    clearDialog();
    onSuccess();
    setLoadingUpdate(false);
  };

  const save = async () => {
    if (form.existingFileId) {
      addToPage();
      return;
    }

    setLoadingUpdate(true);
    let resp;
    try {
      const additionals = {};
      const additionalAttributes = {};
      if (isEditing) {
        additionals.fileId = fileId;
      }
      if (addToPageId) {
        additionals.addToPageId = addToPageId;
      }

      // We can only have one of blobId or linkUrl, otherwise we'll get an
      // error from the API
      if (form.blobId) {
        additionalAttributes.blobId = form.blobId;
      } else {
        additionalAttributes.linkUrl = form.linkUrl;
      }

      resp = await WSGQL.mutate({
        mutation: isEditing ? UPDATE_FILE_MUTATION : CREATE_FILE_MUTATION,
        variables: {
          ...additionals,
          clientId,
          addToPageId,
          attributes: {
            name: form.name,
            notes: form.notes,
            identifier: form.identifier,
            filename: form.filename,
            confidential: form.confidential,
            ...additionalAttributes,
          },
        },
      });
    } catch (error) {
      setLoadingUpdate(false);
      showGQLError({error});
      return;
    }

    const data = isEditing ? resp.data.updateFile : resp.data.createFile;

    if (data.errors.length > 0) {
      showWSErrors({errors: data.errors});
    } else {
      showSuccess({message: 'File saved'});
      clearDialog();
      onSuccess();
    }

    setLoadingUpdate(false);
  };

  const deleteFromAll = async () => {
    try {
      await WSGQL.mutate({
        mutation: DELETE_ENTIRELY_MUTATION,
        variables: {fileId, clientId},
      });
    } catch (error) {
      showGQLError({error});
      return;
    }

    showSuccess({message: 'File deleted from all pages and guides.'});
    clearDialog();
    onSuccess();
  };

  const copyId = () => {
    navigator.clipboard.writeText(fileId);
    showSuccess({message: 'File ID copied'});
  };

  ////
  // Render
  //
  const previewStyle = form.thumbnailUrl
    ? {backgroundImage: `url('${form.thumbnailUrl}')`}
    : {};

  const loading = uploadingFile || loadingFile || loadingUpdate;
  const disableSave = loading
    || (!isEditing && !form.blobId && !form.linkUrl && !form.existingFileId);

  const uploadFileTab = (
    <Fragment>
      <p>
        Note that uploading a new file will replace the current version across
        all guides/pages where the file appears.
      </p>
      <div className="file-edit--file">
        {isEditing && (
          <div
            className="file-edit--file-preview"
            style={previewStyle}
            onClick={openFile}
          >
            {!form.thumbnailUrl && (
              <Icon icon="document" iconSize={48} />
            )}
          </div>
        )}
        {(!uploadingFile && !form.blobId) && (
          <div {...getRootProps()} className="file-edit--file-dropzone">
            <input {...getInputProps()} />
            {!isDragActive && (
              <p>Drag file here or click to upload.</p>
            )}
            {isDragActive && (
              <p>Drop file here</p>
            )}
          </div>
        )}
        {uploadingFile && (
          <div className="file-edit--file-uploading">
            <Spinner value={uploadPerc} />
            Uploading file ({Math.round(uploadPerc * 100)}%)...
          </div>
        )}
        {(form.blobId && isEditing) && (
          <div className="file-edit--file-uploading">
            <Icon icon="tick" intent={Intent.SUCCESS} iconSize={48} />
            The existing file will be replaced upon clicking save.
          </div>
        )}
        {(form.blobId && !isEditing) && (
          <div className="file-edit--file-uploading">
            <Icon icon="tick" intent={Intent.SUCCESS} iconSize={48} />
            Your file is uploaded and will be persisted upon clicking save.
          </div>
        )}
      </div>
    </Fragment>
  );

  const linkTab = (
    <FormGroup label="Link URL">
      <InputGroup
        placeholder="https://example.com"
        value={form.linkUrl}
        onChange={e => setForm({...form, linkUrl: e.target.value})}
      />
    </FormGroup>
  );

  const copyTab = (
    <Card className="file-edit--copy-container">
      <InputGroup
        placeholder="Search for file by name or part number"
        value={fileSearchQuery}
        onChange={e => setFileSearchQuery(e.target.value)}
        leftIcon="search"
      />
      <div className="file-edit--results-container">
        {loadingSearch && (
          <Spinner />
        )}
        {!loadingSearch && fileSearchResults?.length === 0 && (
          <p>No results found</p>
        )}
        {!loadingSearch && !fileSearchResults && (
          <p>Enter a query to search</p>
        )}
        {!loadingSearch && fileSearchResults?.map((file) => {
          return (
            <div
              key={file.id}
              className={classNames({
                'file-edit--copy-file-preview': true,
                selected: form.existingFileId === file.id,
              })}
              onClick={() => selectExistingFile(file)}
              title={`${file.name}${file.identifier ? ` (${file.identifier})` : ''}`}
            >
              {file.thumbnailUrl && (
                <div
                  className="file-edit--copy-file-image"
                  style={{backgroundImage: `url('${file.thumbnailUrl}')`}}
                />
              )}
              {!file.thumbnailUrl && (
                <div className="file-edit--copy-file-image">
                  <Icon icon="document" size={36} />
                </div>
              )}
              <div className="file-edit--copy-file-meta">
                {file.name}{file.identifier ? ` (${file.identifier})` : ''}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );

  return (
    <Fragment>
      <Drawer
        isOpen={isOpen}
        icon="document"
        title={(isEditing
          ? <span onClick={copyId}>Edit file</span>
          : 'New file'
        )}
        onClose={clearDialog}
      >
        <div className={Classes.DRAWER_BODY}>
          <div className={Classes.DIALOG_BODY}>
            <H3>Basic Information</H3>
            <FormGroup label="Name" labelInfo="(required)">
              <InputGroup
                placeholder="File Name"
                value={form.name}
                onChange={e => setForm({...form, name: e.target.value})}
                disabled={form.existingFileId || loading}
                className={classNames({[Classes.SKELETON]: loadingFile})}
              />
            </FormGroup>
            <FormGroup label="Part #">
              <InputGroup
                placeholder="Part # (optional, must be unique)"
                value={form.identifier}
                onChange={e => setForm({...form, identifier: e.target.value})}
                disabled={form.existingFileId || loading}
                className={classNames({[Classes.SKELETON]: loadingFile})}
              />
            </FormGroup>
            <FormGroup label="Notes">
              <TextArea
                fill
                placeholder="Share any additional details with the team (optional)"
                value={form.notes}
                onChange={e => setForm({...form, notes: e.target.value})}
                disabled={form.existingFileId || loading}
                className={classNames({[Classes.SKELETON]: loadingFile})}
              />
            </FormGroup>
            <FormGroup
              label="Confidentiality"
              subLabel="Files marked as confidential are visible in guides but not allowed to be shared nor downloaded."
            >
              <Checkbox
                label="Mark confidential"
                checked={form.confidential}
                disabled={form.existingFileId || loading}
                onChange={e => setForm({...form, confidential: e.target.checked})}
              />
            </FormGroup>
            <Tabs
              selectedTabId={fileType}
              onChange={changeFileType}
              className="file-edit--tabs"
            >
              {((!isEditing && !form.existingFileId) || fileType === FileType.FILE) && (
                <Tab
                  id={FileType.FILE}
                  title="Upload File"
                  panel={uploadFileTab}
                />
                )}
              {(!isEditing && !form.blobId && !uploadingFile) && (
                <Tab
                  id={FileType.COPY}
                  title="Existing File"
                  panel={copyTab}
                />
              )}
              {((!isEditing && !uploadingFile && !form.blobId && !form.existingFileId)
                || fileType === FileType.LINK) && (
                <Tab
                  id={FileType.LINK}
                  title="External Link"
                  panel={linkTab}
                />
              )}
            </Tabs>
            {isEditing && (
              <Fragment>
                <H3>Locations</H3>
                {form.pages?.length === 0 && (
                  <p>This file does not appear in any guides</p>
                )}
                {form.pages?.map((page) => {
                  return (
                    <div key={page.id} className="file-edit--page">
                      <Button
                        minimal
                        icon="book"
                        text={page.guide.name}
                        onClick={() => {
                          window.open(`/clients/${clientId}/guides/${page.guide.id}/edit`, '_blank');
                        }}
                      />
                      <Icon icon="chevron-right" />
                      <Button
                        minimal
                        icon="document"
                        text={page.name}
                        onClick={() => {
                          if (page.pageType === 'section') {
                            window.open(`/clients/${clientId}/guides/${page.guide.id}/pages/${page.parentId}/edit`, '_blank');
                          } else {
                            window.open(`/clients/${clientId}/guides/${page.guide.id}/pages/${page.id}/edit`, '_blank');
                          }
                        }}
                      />
                    </div>
                  );
                })}
              </Fragment>
            )}
          </div>
        </div>
        <div className={Classes.DRAWER_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            {isEditing && (
              <Button
                minimal
                disabled={loading}
                onClick={() => setShowDeleteWarning(true)}
                text="Delete file"
                intent={Intent.DANGER}
              />
            )}
            <Button
              disabled={loading}
              text="Close"
              onClick={clearDialog}
            />
            <Button
              disabled={disableSave}
              loading={loadingUpdate}
              intent={Intent.PRIMARY}
              text="Save"
              onClick={save}
            />
          </div>
        </div>
      </Drawer>
      <Alert
        isOpen={showDeleteWarning}
        onClose={() => setShowDeleteWarning(false)}
        onConfirm={deleteFromAll}
        icon="warning-sign"
        cancelButtonText="Cancel"
        confirmButtonText="Delete file"
        intent={Intent.DANGER}
      >
        {form.pages?.length > 0 && (
          <Fragment>
            <p>
              Are you sure you want to delete this file? <b>This will delete
              the file across all guides and pages within your
              organization.</b> This file currently appears in {` ${form.pages.length} `}
              locations.
            </p>
            <p>
              If you wish to remove this file from a page, cancel this dialog,
              return to the page, and click the X icon in the top right of the
              file's thumbnail.
            </p>
            <p>
              If you choose to continue deleting this file, note that this action
              cannot be undone.
            </p>
          </Fragment>
        )}
        {form.pages?.length === 0 && (
          <p>
            Are you sure you want to delete this file? This action cannot be
            undone.
          </p>
        )}
      </Alert>
    </Fragment>
  );
};

export default FileEditDialog;
